/**
 * mutationParams.ts - Shared dependency bundle and plumbing for table mutation methods.
 *
 * Each mutation method (see the sibling files in this directory) is a plain function whose first
 * argument is a {@link TableMutationParams} bundle, followed by its own call arguments.
 * `createTableMutations` binds `params` into each method once per table. Keeping these as plain
 * functions (rather than hooks) makes them unit-testable with a fake `params` and lets dependent
 * methods call their siblings directly.
 */

import DocumentStore from 'notebook/documents/DocumentStore';
import type {Table} from 'notebook/tables/constants/TableConstants';
import type {TableEditableState} from 'notebook/tables/core-ui/TableEditableState';
import type {Selector} from 'notebook/tables/excess/EvaluationConstants';
import type {TableOpDiff, TableOpDiffMap} from 'notebook/tables/excess/ops/TableOpConstants';
import type {useTableOps} from 'notebook/tables/spreadsheets/TableOpContext';
import type {TableNoteContextInterface} from 'notebook/tables/store/contexts/TableNoteContext';
import type {TableModel} from 'notebook/tables/store/models/TableModel';
import type {TableProps} from 'notebook/tables/store/TableStore';
import type {WorksheetInfo} from 'notebook/tables/util/analytics/CrossStepReferenceAnalytics';
import {getWorksheetIdFromNamespace} from 'notebook/tables/util/WorksheetTableNamespaceUtil';
import type {Optional} from 'util/Types';

/**
 * Dependencies threaded into every mutation method. Built once per table by `TableModelProvider` and passed as
 * the first argument to each method. All model data and props are read fresh via {@link getTableModel} and
 * {@link getTableProps} at call time, so the bundle itself stays stable across renders.
 */
export interface TableMutationParams {
  /** Reads the latest {@link TableModel} from the store. Always call this at the top of a method. */
  getTableModel: () => TableModel;
  /** Reads the latest {@link TableProps} from the store. Call this at the top of a method that needs props. */
  getTableProps: () => TableProps;
  /** Writes partial editable state to the store (selection, dragging, etc.). */
  setState: (state: Partial<TableEditableState>) => void;
  /**
   * The note context for the mounted surface (entry/worksheet/template), or `null` in isolated
   * contexts (tests/harnesses). Used by merge validation to scan the whole note for references.
   */
  tableNoteContext: TableNoteContextInterface | null;
  /** The underlying table ops (reorder, insert/delete rows/columns, merge, etc.). */
  tableOps: ReturnType<typeof useTableOps>;
}

/**
 * Persist a table mutation result via the table's `updateHandler`, wrapping the tableOp in the
 * `{[tableName]: tableOp}` map shape the handler expects (omitted when there is no op).
 */
export function persistTable(
  params: TableMutationParams,
  result: {table: Table; tableOp: TableOpDiff | null},
  selectors?: Optional<Array<Selector>>
): void {
  const {table, index, updateHandler} = params.getTableProps();
  const tableOp: TableOpDiffMap | undefined =
    result.tableOp != null ? {[table.name]: result.tableOp} : undefined;
  updateHandler(index, {table: result.table}, selectors, tableOp);
}

/**
 * Compute the {@link WorksheetInfo} for cross-step reference analytics, or `null` when the table's
 * namespace does not correspond to a worksheet.
 */
export function computeWorksheetInfo(tableNamespace: string, fileId: string): WorksheetInfo | null {
  const worksheetId = getWorksheetIdFromNamespace(tableNamespace);
  return worksheetId ? {worksheetId, stepCount: DocumentStore.getRelatedDocumentIds(fileId).length} : null;
}
